import { Input, InputNumber, Switch, Typography } from "antd";
import { CvConfigDto } from "api/generated";
import {
  DEFAULT_PERIODS,
  HumanCronExpressionInput,
} from "./HumanCronExpressionInput";
import { useState } from "react";

export interface ConfigFieldProps {
  config: CvConfigDto;
  value: string;
  readOnly: boolean;
  onChange: (value: string) => void;
}

function ReadOnlyText({
  value,
  isRequired,
}: {
  value: string;
  isRequired?: boolean;
}) {
  if (!value) {
    return isRequired ? (
      <Typography.Text type="danger" style={{ fontStyle: "italic" }}>
        (requerido - sin valor)
      </Typography.Text>
    ) : (
      <Typography.Text type="secondary">(sin valor)</Typography.Text>
    );
  }

  return <Typography.Text>{value}</Typography.Text>;
}

// ─── Boolean Field ─────────────────────────────────────────────────────────────

export function BooleanField({ value, readOnly, onChange }: ConfigFieldProps) {
  const checked = value === "true";

  if (readOnly) {
    return value ? (
      <Typography.Text>{checked ? "Activado" : "Desactivado"}</Typography.Text>
    ) : (
      <Typography.Text type="secondary">(sin valor)</Typography.Text>
    );
  }

  return (
    <Switch
      checked={checked}
      checkedChildren="Activado"
      unCheckedChildren="Desactivado"
      onChange={(v) => onChange(v ? "true" : "false")}
    />
  );
}

// ─── Integer Field ─────────────────────────────────────────────────────────────

export function IntegerField({
  config,
  value,
  readOnly,
  onChange,
}: ConfigFieldProps) {
  if (readOnly) {
    return <ReadOnlyText value={value} isRequired={config.isRequired} />;
  }

  return (
    <InputNumber
      value={Number(value)}
      min={config.minValue != null ? Number(config.minValue) : undefined}
      max={config.maxValue != null ? Number(config.maxValue) : undefined}
      onChange={(v) => onChange(String(v ?? ""))}
    />
  );
}

// ─── PasswordField ────────────────────────────────────────────────────────────

export function PasswordField({ value, readOnly, onChange }: ConfigFieldProps) {
  // value es "TRUE" si hay algo configurado, "FALSE" si no
  if (readOnly) {
    return (
      <Typography.Text type={value === "TRUE" ? "secondary" : "danger"}>
        {value === "TRUE" ? "Configurado" : "No configurado"}
      </Typography.Text>
    );
  }

  return (
    <Input.Password
      onChange={(e) => onChange(e.target.value)}
      placeholder="Ingresa la nueva contraseña"
    />
  );
}

// ─── CertificateField / PrivateKeyField ───────────────────────────────────────
function SensitiveFileField({
  value,
  readOnly,
  onChange,
  placeholder,
}: ConfigFieldProps & { placeholder: string }) {
  if (readOnly) {
    return (
      <Typography.Text type={value === "TRUE" ? "secondary" : "danger"}>
        {value === "TRUE" ? "Configurado" : "No configurado"}
      </Typography.Text>
    );
  }

  return (
    <Input.TextArea
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      placeholder={placeholder}
      style={{ fontFamily: "monospace", fontSize: 12 }}
    />
  );
}

export function CertificateField(props: ConfigFieldProps) {
  return (
    <SensitiveFileField
      {...props}
      placeholder="Pega el contenido del certificado (.cer en base64)"
    />
  );
}

export function PrivateKeyField(props: ConfigFieldProps) {
  return (
    <SensitiveFileField
      {...props}
      placeholder="Pega el contenido de la clave privada (.key en base64)"
    />
  );
}

// ─── CronField ────────────────────────────────────────────────────────────────

export function CronField({
  config,
  value,
  readOnly,
  onChange,
}: ConfigFieldProps) {
  return (
    <HumanCronExpressionInput
      value={value}
      readOnly={readOnly}
      onChange={onChange}
      showAdvancedModeToggle
      availablePeriods={DEFAULT_PERIODS}
      config={config}
    />
  );
}

// ─── ListField ────────────────────────────────────────────────────────────────

export function ListField({
  config,
  value,
  readOnly,
  onChange,
}: ConfigFieldProps) {
  const [error, setError] = useState<string | undefined>();

  const COMMA_SEPARATED_REGEX = /^[^,]+(?:\s*,\s*[^,]+)*$/;

  function validate(raw: string): string | undefined {
    if (!raw.trim()) return undefined;
    if (config.validationRegex) {
      return new RegExp(config.validationRegex).test(raw)
        ? undefined
        : (config.validationMessage ?? "Formato inválido");
    }
    return COMMA_SEPARATED_REGEX.test(raw.trim())
      ? undefined
      : "Debe ser una lista de valores separados por comas";
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const raw = e.target.value;
    setError(validate(raw));
    onChange(raw);
  }

  if (readOnly) {
    // Mostrar como lista de tags o texto plano según longitud
    const items = value
      ? value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    if (!items.length)
      return <Typography.Text type="secondary">(sin valor)</Typography.Text>;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {items.map((item) => (
          <Typography.Text key={item} code>
            {item}
          </Typography.Text>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Input.TextArea
        value={value}
        onChange={handleChange}
        rows={2}
        placeholder="valor1, valor2, valor3"
        status={error ? "error" : undefined}
      />
      <Typography.Text type={error ? "danger" : "secondary"}>
        {error ?? "Ingresa los valores separados por comas"}
      </Typography.Text>
    </div>
  );
}

// ─── StringField (default) ────────────────────────────────────────────────────

export function StringField({
  value,
  readOnly,
  onChange,
  config,
}: ConfigFieldProps) {
  if (readOnly) {
    return <ReadOnlyText value={value} isRequired={config.isRequired} />;
  }

  return <Input value={value} onChange={(e) => onChange(e.target.value)} />;
}

// ─── Resolver ─────────────────────────────────────────────────────────────────
// Dado un CvConfig, devuelve el componente de campo solicitado.

export function ConfigField(props: ConfigFieldProps) {
  switch (props.config.dataType) {
    case "BOOLEAN":
      return <BooleanField {...props} />;
    case "INTEGER":
      return <IntegerField {...props} />;
    case "PASSWORD":
      return <PasswordField {...props} />;
    case "CERTIFICATE":
      return <CertificateField {...props} />;
    case "PRIVATE_KEY":
      return <PrivateKeyField {...props} />;
    case "CRON":
      return <CronField {...props} />;
    case "LIST":
      return <ListField {...props} />;
    default:
      return <StringField {...props} />;
  }
}
